import React, { useState, useEffect } from "react";
import { Play, Loader, X, FileText, Image as ImageIcon } from "lucide-react";
import Header from "../../components/common/Header";

const Portfolio = () => {
  const [portfolioData, setPortfolioData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  useEffect(() => {
    if (selectedMedia) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedMedia]);

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/portfolio/get-all`
      );
      const data = await response.json();

      if (data.success) {
        setPortfolioData(data.data);
        setError(null);
      } else {
        setError("Failed to fetch portfolio");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching portfolio");
      console.error("Error fetching portfolio:", err);
    } finally {
      setLoading(false);
    }
  };

  // Full-screen media player
  const FullScreenPlayer = ({ media, onClose }) => {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
        >
          <X className="w-8 h-8" />
        </button>
        {media.mediaType === "video" ? (
          <video
            src={media.url}
            controls
            autoPlay
            className="w-full h-full object-contain"
          >
            Your browser does not support the video tag.
          </video>
        ) : media.mediaType === "image" ? (
          <img
            src={media.url}
            alt="Portfolio item"
            className="w-full h-full object-contain"
          />
        ) : null}
      </div>
    );
  };

  // Media item component
  const MediaItem = ({ mediaItem, title }) => {
    const handleClick = () => {
      if (mediaItem.mediaType === "pdf") {
        window.open(mediaItem.url, "_blank");
      } else {
        setSelectedMedia(mediaItem);
      }
    };

    if (mediaItem.mediaType === "image") {
      return (
        <div
          onClick={handleClick}
          className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group cursor-pointer bg-gray-200"
        >
          <img
            src={mediaItem.url}
            alt={title}
            className="w-full h-48 sm:h-56 md:h-64 object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
            <div className="flex items-center text-white">
              <ImageIcon className="w-5 h-5 mr-2" />
              <span className="font-medium">View Image</span>
            </div>
          </div>
        </div>
      );
    }

    if (mediaItem.mediaType === "video") {
      return (
        <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group bg-gray-300">
          <video
            src={mediaItem.url}
            preload="auto"
            muted={false}
            playsInline
            controls
            className="w-full h-48 sm:h-56 md:h-64 object-cover transition-transform duration-500 group-hover:scale-[1.02] bg-gray-900"
            onLoadedMetadata={(e) => {
              e.target.currentTime = 1;
            }}
          />
        </div>
      );
    }

    if (mediaItem.mediaType === "pdf") {
      return (
        <div
          onClick={handleClick}
          className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group cursor-pointer bg-gradient-to-br from-red-50 to-red-100 h-48 sm:h-56 md:h-64 flex flex-col items-center justify-center"
        >
          <FileText className="w-16 h-16 text-red-600 mb-4 group-hover:scale-110 transition-transform" />
          <p className="text-gray-700 font-semibold text-lg px-4 text-center">
            {title}
          </p>
          <p className="text-gray-500 text-sm mt-2">Click to open PDF</p>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader className="w-12 h-12 text-red-600 animate-spin" />
          <p className="text-gray-600 font-semibold">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 font-semibold text-lg mb-4">{error}</p>
          <button
            onClick={fetchPortfolio}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const activeData = portfolioData[activeCategory] || {};
  const portfolioItems = activeData.portfolioItems || [];

  return (
    <div className="w-full bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8 min-h-screen">
      <Header
        breadcrumbs={[
          { label: "HOME", path: "/" },
          { label: "STUDENTS PORTFOLIO", path: "/portfolio" },
        ]}
        title="Students Portfolio"
        description=" Explore the creativity, talent, and achievements of our students through their portfolios. Each portfolio showcases projects, performances, and activities that highlight their learning journey, innovation, and passion. From academic excellence to artistic expression, these portfolios reflect the diverse skills and dedication of our learners."
      />
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold bg-gradient-to-r from-gray-900 via-red-600 to-blue-900 bg-clip-text text-transparent mb-4 heading">
          Students Portfolio
        </h1>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto sub-heading">
          Explore our students' exceptional work across various design
          disciplines
        </p>
      </div>

      {/* Category Tabs */}
      {portfolioData.length > 0 && (
        <div className="w-full mx-auto mb-12">
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {portfolioData.map((category, index) => (
              <button
                key={category._id}
                onClick={() => setActiveCategory(index)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                  activeCategory === index
                    ? "bg-gradient-to-r from-red-600 to-secondaryColor text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-gray-100 shadow"
                }`}
              >
                {category.categoryName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Portfolio Content */}
      <div className="w-full mx-auto">
        {portfolioItems.length > 0 ? (
          <div className="space-y-16">
            {portfolioItems.map((item) => (
              <div key={item._id} className="space-y-6">
                {/* Portfolio Item Title */}
                <h2 className="text-3xl font-bold text-gray-800 border-b-4 border-red-600 pb-3 inline-block">
                  {item.title}
                </h2>

                {/* Media Grid */}
                {item.media && item.media.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
                    {item.media.map((mediaItem) => (
                      <MediaItem
                        key={mediaItem._id}
                        mediaItem={mediaItem}
                        title={item.title}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">
                    No media available for this item
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">
              No portfolio items available in this category
            </p>
          </div>
        )}
      </div>

      {/* Full-Screen Media Player */}
      {selectedMedia && (
        <FullScreenPlayer
          media={selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}
    </div>
  );
};

export default Portfolio;
